"use client";

// 공용 위치 훅: 웹 전용
// - 권한 상태: 'checking' | 'granted' | 'denied' | 'unavailable' | 'prompt'
// - 단발성 위치 조회(getCurrentPosition)
// - 지속 추적(watchPosition/startWatch, stopWatch)

import { useCallback, useEffect, useRef, useState } from "react";

// 외부에 노출할 권한 상태 타입
export type LocationPermissionStatus =
	| "checking"
	| "granted"
	| "denied"
	| "unavailable"
	| "prompt";

// 훅 반환 타입
export type UseLocationResult = {
	permissionStatus: LocationPermissionStatus;
	position: GeolocationPosition | null;
	isLoading: boolean;
	// 권한/위치 관련 함수들
	checkPermission: () => Promise<LocationPermissionStatus | undefined>;
	requestPermission: () => Promise<LocationPermissionStatus | undefined>;
	getCurrentPosition: (
		opts?: PositionOptions,
	) => Promise<GeolocationPosition | null>;
	startWatch: (opts?: PositionOptions) => Promise<() => void>;
	stopWatch: () => Promise<void>;
};

// SSR 안전 체크
const isClient = () => typeof window !== "undefined";

export function useLocation(): UseLocationResult {
	const [permissionStatus, setPermissionStatus] =
		useState<LocationPermissionStatus>("checking");
	const [position, setPosition] = useState<GeolocationPosition | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	// 웹 watch id 보관
	const webWatchIdRef = useRef<number | null>(null);

	const checkPermission = useCallback(async () => {
		if (!isClient()) return;

		if (!("geolocation" in navigator)) {
			setPermissionStatus("unavailable");
			return "unavailable";
		}

		const canUsePermissions =
			typeof navigator.permissions !== "undefined" &&
			typeof (navigator.permissions as any).query === "function";
		if (canUsePermissions) {
			try {
				const result = await navigator.permissions.query({
					name: "geolocation" as PermissionName,
				});
				const mapped: LocationPermissionStatus = result.state as any; // 'granted' | 'denied' | 'prompt'
				setPermissionStatus(mapped);
				return mapped;
			} catch (e) {
				console.warn(
					"[location] permissions API 조회 실패, 직접 요청 필요:",
					e,
				);
				setPermissionStatus("prompt");
				return "prompt";
			}
		} else {
			// 미지원 브라우저: 실제 요청을 하기 전 상태는 알 수 없으므로 prompt로 간주
			setPermissionStatus("prompt");
			return "prompt";
		}
	}, []);

	const getCurrentPosition = useCallback(async (opts?: PositionOptions) => {
		if (!isClient()) return null;

		setIsLoading(true);
		try {
			if (!("geolocation" in navigator)) {
				setPermissionStatus("unavailable");
				return null;
			}
			const w3c = await new Promise<GeolocationPosition>((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(
					(p) => resolve(p),
					(err) => reject(err),
					{
						enableHighAccuracy: false,
						timeout: 10000,
						maximumAge: 300000,
						...opts,
					},
				);
			});
			setPosition(w3c);
			setPermissionStatus("granted");
			return w3c;
		} catch (error) {
			console.error("[location] 현재 위치 가져오기 실패:", error);
			setPermissionStatus("denied");
			return null;
		} finally {
			setIsLoading(false);
		}
	}, []);

	const requestPermission = useCallback(async () => {
		if (!isClient()) return;

		// 웹은 명시적인 권한 요청 API가 없고 getCurrentPosition 호출 시 브라우저가 프롬프트를 띄움
		if (!("geolocation" in navigator)) {
			setPermissionStatus("unavailable");
			return "unavailable";
		}
		const pos = await getCurrentPosition();
		return pos ? "granted" : permissionStatus;
	}, [getCurrentPosition, permissionStatus]);

	const startWatch = useCallback(
		async (opts?: PositionOptions) => {
			if (!isClient()) return async () => {};

			// 시작 전 권한 확인/요청 시도
			const status = await checkPermission();
			if (status !== "granted") {
				const req = await requestPermission();
				if (req !== "granted") {
					return async () => {};
				}
			}

			if (!("geolocation" in navigator)) {
				setPermissionStatus("unavailable");
				return async () => {};
			}
			const id = navigator.geolocation.watchPosition(
				(p) => {
					setPosition(p);
					setPermissionStatus("granted");
				},
				(err) => {
					console.error("[location] 웹 watch 오류:", err);
				},
				{ enableHighAccuracy: false, maximumAge: 300000, ...opts },
			);
			webWatchIdRef.current = id;
			return async () => {
				if (webWatchIdRef.current !== null) {
					navigator.geolocation.clearWatch(webWatchIdRef.current);
					webWatchIdRef.current = null;
				}
			};
		},
		[checkPermission, requestPermission],
	);

	const stopWatch = useCallback(async () => {
		if (!isClient()) return;
		try {
			if (webWatchIdRef.current !== null) {
				navigator.geolocation.clearWatch(webWatchIdRef.current);
				webWatchIdRef.current = null;
			}
		} catch (e) {
			console.error("[location] watch 정지 실패:", e);
		}
	}, []);

	// 마운트 시 1) 권한 체크 2) 웹 권한 변경 이벤트 구독
	useEffect(() => {
		if (!isClient()) return;
		let isMounted = true;

		(async () => {
			const st = await checkPermission();
			// 권한이 바로 granted면 즉시 현재 위치 1회 조회
			if (isMounted && st === "granted") {
				await getCurrentPosition();
			}
		})();

		const setupPermissionChangeListener = async () => {
			try {
				if (typeof navigator.permissions === "undefined") return () => {};
				const perm = await navigator.permissions.query({
					name: "geolocation" as PermissionName,
				});
				const handler = () => {
					const state = perm.state as LocationPermissionStatus;
					setPermissionStatus(state);
					if (state === "granted") {
						// granted로 바뀌면 한번 가져오기
						getCurrentPosition();
					}
				};
				perm.addEventListener("change", handler);
				return () => perm.removeEventListener("change", handler);
			} catch (e) {
				console.warn("[location] 웹 권한 변경 감지 설정 실패:", e);
				return () => {};
			}
		};

		let cleanup: (() => void) | undefined;
		setupPermissionChangeListener().then((fn) => {
			cleanup = fn;
		});

		return () => {
			isMounted = false;
			if (cleanup) cleanup();
		};
	}, [checkPermission, getCurrentPosition]);

	// 언마운트 시 watch 정리
	useEffect(() => {
		return () => {
			if (webWatchIdRef.current !== null) {
				try {
					navigator.geolocation.clearWatch(webWatchIdRef.current);
				} catch {}
				webWatchIdRef.current = null;
			}
		};
	}, []);

	return {
		permissionStatus,
		position,
		isLoading,
		checkPermission,
		requestPermission,
		getCurrentPosition,
		startWatch,
		stopWatch,
	};
}

export default useLocation;
